/**
 * A NAV-KULCSBEVITEL ABLAKA — a PALETTA gyorsítása.
 *
 * ⚠️ EZ MÁR NEM AZ EGYETLEN ÚT (#25/4, Viktor artboardon, 2026-09-13). A
 * kulcsbevitel FŐ helye a Beállítások lap NAV és Tensoft szelete
 * (`NavSettings`): ott a kereső és a mezők egyszerre látszanak, és nincs
 * réteg a réteg fölött. Ez az ablak megmarad, mert a `Ctrl+K` → „NAV-kulcsok
 * beállítása" út rövidebb, mint a lapra belépni — de a TARTALMA ugyanaz a
 * három darab (`NavCredentialParts`), ugyanabból az állapotból
 * (`useNavCredentialEditor`).
 *
 * ⚠️ EZ NEM NEGYEDIK FELÜLET. A `CLAUDE.md` §2 szerint negyedik felület
 * nincs, és a IV/D3 kifejezetten kimondja: NINCS céglap-képernyő, mert az
 * negyedik felület volna. Ez egy paletta-művelet ablaka, nem egy oldal,
 * amire navigálni lehet.
 *
 * ═══ A CÉGKERESŐ AZ ABLAKBAN VAN (Viktor, 2026-09-10) ═══
 *
 * A IV/D3 szövege „a kiválasztott céghez" kötött műveletet mond — de
 * cégválasztó MA NINCS: a `commands.ts` fejléce kimondja, hogy a cég-szűrő a
 * szerver kontextusából jön (`RequestContext`, 2.6/23), és váltani rajta a
 * felület nem tud. A döntés ezért az, hogy a kereső IDE kerül, az ablak első
 * mezőjeként. Így a shell-cégválasztó megépítése nem előfeltétel, és ha
 * egyszer megépül, ez az űrlap változatlanul használható marad.
 *
 * ═══ MIÉRT MODAL, AMIKOR A §3 A MODALT TILTJA ═══
 *
 * A `CLAUDE.md` §3 nem-célja: „modal a bizonylat-FELDOLGOZÁSHOZ — a modal
 * egérre való, ez billentyűzetre". Az a tilalom a NAPI, ISMÉTLŐDŐ munkára
 * szól, ahol a modal minden tételnél egy plusz nyitás-zárás. A kulcsbevitel
 * ennek az ellentéte: cégenként EGYSZER történik meg, egy ~80 elemű
 * életciklusban. És ugyanaz a párbeszéd-alak, amit a paletta és a súgó is
 * használ — billentyűvel járható végig, `Escape`-pel kijárattal.
 *
 * ═══ ÍRHATÓ, DE NEM OLVASHATÓ ═══
 *
 * A négy mező MINDIG ÜRESEN nyílik, akkor is, ha a cégnél már van bekötés — a
 * II/9 második alsó korlátja szerint értéket visszaolvasni nem lehet. Ez a
 * felületen MEGZAVARÓ lenne magyarázat nélkül („elvesztek a kulcsaim?"),
 * ezért mondja ki a `writeOnlyNotice`, és ezért mutat a kereső „be van
 * kötve" jelzést: az az egyetlen visszajelzés, amit adhatunk.
 */

import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { useDialogFocus } from '@/hooks/useDialogFocus';
import {
  NavCompanyPicker,
  NavCredentialFields,
  NavCredentialNotices,
  NavCredentialSaved,
} from './NavCredentialParts';
import { useNavCredentialEditor } from './useNavCredentialEditor';

interface NavCredentialsDialogProps {
  onClose: () => void;
}

export function NavCredentialsDialog({ onClose }: NavCredentialsDialogProps) {
  const { t } = useTranslation();
  const editor = useNavCredentialEditor();
  const listId = useId();
  // A FÓKUSZ BEJÖN A CÉGKERESŐRE, ÉS BENT MARAD (#16/2). Félig beírt
  // NAV-kulcsoknál egy kiszökő `Tab` a mögöttes listára vinne, ahol a
  // leütések már parancsok — a kulcs fele meg a mezőben maradna.
  const dialog = useDialogFocus<HTMLDivElement>();

  return (
    <div className="overlay">
      {/* A háttér SAJÁT GOMB, nem kattintható `<div>` — ugyanaz az érvelés,
          mint a palettán: egy `onClick`-es üres elem a képernyőolvasónak
          láthatatlan, a billentyűzetnek elérhetetlen. */}
      <button
        type="button"
        className="overlay__backdrop"
        aria-label={t('navCredentials.close')}
        tabIndex={-1}
        onClick={onClose}
      />

      <div
        className="card credentials"
        ref={dialog}
        role="dialog"
        aria-modal="true"
        aria-label={t('navCredentials.title')}
        onKeyDown={(event) => {
          // ⚠️ `stopPropagation` — a `useShortcuts` a `window`-on ül. Enélkül
          // az `Escape` a súgót is csukná, a `Ctrl+1` pedig a hiánylistára
          // navigálna KI az ablakból, félig beírt kulcsokkal.
          if (event.key === 'Escape') {
            event.preventDefault();
            event.stopPropagation();
            onClose();
          }
        }}
      >
        <h2>{t('navCredentials.title')}</h2>

        <NavCredentialNotices status={editor.status} detail={editor.detail} />
        <NavCredentialSaved editor={editor} />
        <NavCompanyPicker editor={editor} listId={listId} />
        <NavCredentialFields editor={editor} listId={listId} />
      </div>
    </div>
  );
}
